import { $codeWords, logs } from './logbook';
import { $operators } from './operators';
import {
  $assignment,
  $currentOperator,
  $date,
  $defaultFrequency,
  $myCallsign,
} from './settings';
import { $tags } from './tags';
import type { Log, Tag } from '@/data/logs';
import type { Operator } from '@/data/operator';

export type ExportedLogbookContext = {
  logs: Log[];
  assignment: string;
  date: string;
  operators: Operator[];
  operator: Operator;
  myCallsign: string;
  defaultFrequency: string;
  tags: Tag[];
  codeWords?: Record<string, string>;
};

/**
 * Export the current logbook context for saving/sharing
 */
export function exportLogbookContext(): ExportedLogbookContext {
  return {
    logs: logs.toJSON(),
    assignment: $assignment.get(),
    date: $date.get(),
    operators: $operators.get(),
    operator: $currentOperator.get(),
    myCallsign: $myCallsign.get(),
    defaultFrequency: $defaultFrequency.get(),
    tags: $tags.get(),
    codeWords: $codeWords.get(),
  };
}

/**
 * Import a logbook context from a saved file
 */
export function importLogbookContext(data: ExportedLogbookContext) {
  logs.loadFromJSON(data.logs);
  $assignment.set(data.assignment);
  $date.set(data.date);
  $operators.set(data.operators);
  $currentOperator.set(data.operator);
  $myCallsign.set(data.myCallsign);
  $defaultFrequency.set(data.defaultFrequency);
  $codeWords.set(data.codeWords || {});

  // Tags are global; merge imported tags into existing ones, avoiding duplicates by ID.
  const existingTags = $tags.get();
  const importedTags = data.tags || [];
  const existingIds = new Set(existingTags.map(tag => tag.id));
  const newImportedTags = importedTags.filter(tag => !existingIds.has(tag.id));
  $tags.set([...existingTags, ...newImportedTags]);
}

/**
 * Replace the entire local context with an authoritative snapshot.
 */
export function replaceLogbookContext(data: ExportedLogbookContext) {
  logs.loadFromJSON(data.logs);
  $assignment.set(data.assignment);
  $date.set(data.date);
  $operators.set(data.operators);
  $currentOperator.set(data.operator);
  $myCallsign.set(data.myCallsign);
  $defaultFrequency.set(data.defaultFrequency);
  $tags.set(data.tags || []);
  $codeWords.set(data.codeWords || {});
}
